import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface DayListSettingFieldProps {
  label: string;
  description?: string;
  value: number[];
  onChange: (value: number[]) => void;
}

/**
 * Labeled comma-separated day-list input, used for cadence settings such as the
 * Sales Rep follow-up days. Keeps what the admin typed while editing and
 * normalizes to a sorted, deduped list of positive whole days on blur.
 */
export function DayListSettingField({
  label,
  description,
  value,
  onChange,
}: DayListSettingFieldProps) {
  const [text, setText] = useState(() => value.join(", "));

  const commit = (raw: string) => {
    const days = parseDays(raw);
    setText(days.join(", "));
    onChange(days);
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      <Input
        className="w-[220px]"
        inputMode="numeric"
        placeholder="2, 5, 10"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseDays(e.target.value));
        }}
        onBlur={(e) => commit(e.target.value)}
      />
    </div>
  );
}

/** Parses a comma-separated list into sorted, deduped positive whole days. */
function parseDays(raw: string): number[] {
  const days = raw
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((day) => Number.isInteger(day) && day > 0);
  return [...new Set(days)].sort((a, b) => a - b);
}
